// ============================================================
// Summit League v2 – Dashboard Page
// Main game dashboard – single API call, delegates to components
// ============================================================

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { useGame } from '../hooks/useGame';
import axiosClient from '../api/axiosClient';

// Dashboard components
import SeasonHeader from '../components/dashboard/SeasonHeader';
import LeaderboardPreview from '../components/dashboard/LeaderboardPreview';
import AvailableMatches from '../components/dashboard/AvailableMatches';
import MatchCenter from '../components/dashboard/MatchCenter';
import RecentMatches from '../components/dashboard/RecentMatches';
import GuestCta from '../components/dashboard/GuestCta';
import Modal from '../components/ui/Modal';

// Types
import type {
  DashboardData,
  DashboardApiResponse,
  OpenMatch,
  PendingMatch,
} from '../types/dashboard.types';

// Helpers
import { formatMatchType, formatMatchFormat } from '../components/dashboard/DashboardHelpers';

// Assets
import HaloGrowthGuildLogo from '../assets/Halo-Growth-Guild-Logo.png';
import MythicGamingLogo from '../assets/Mythic-Gaming-Logo.png';
import NoobComboLogo from '../assets/Noob-Combo-Logo.png';

// Styles
import '../styles/dashboard.css';

// ── Accept modal state ────────────────────────────────────

interface AcceptModalState {
  isOpen: boolean;
  match: OpenMatch | PendingMatch | null;
  loading: boolean;
  error: string | null;
}

const INITIAL_MODAL: AcceptModalState = {
  isOpen: false,
  match: null,
  loading: false,
  error: null,
};

// ── Tip of the Hat data ───────────────────────────────────

const TIP_OF_THE_HAT_ORGS = [
  { name: 'Halo Growth Guild', logo: HaloGrowthGuildLogo, href: 'https://x.com/HaloGrowthGuild'    },
  { name: 'Mythic Gaming',     logo: MythicGamingLogo,    href: 'https://x.com/MythicGaming117'    },
  { name: 'Noob Combo',        logo: NoobComboLogo,       href: 'https://www.noobcombo.com/'       },
];

// ── Component ─────────────────────────────────────────────

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { gameId, hasGame, setSeason } = useGame();

  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Re-fetch trigger for post-action refreshes
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  // Accept match modal (only for other teams' matches)
  const [modal, setModal] = useState<AcceptModalState>(INITIAL_MODAL);

  // ── Fetch dashboard data ──────────────────────────────

  useEffect(() => {
    if (!gameId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axiosClient.get<DashboardApiResponse>(
          `/dashboard?game_id=${gameId}`
        );

        if (!cancelled) {
          const dashData = res.data.data ?? res.data;
          setData(dashData as DashboardData);

          if (dashData.season) {
            setSeason(dashData.season);
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || 'Failed to load dashboard.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchDashboard();

    return () => {
      cancelled = true;
    };
  }, [gameId, refreshKey, setSeason]);

  // ── Match action handlers ─────────────────────────────

  const handleMatchAction = useCallback(
    (match: OpenMatch | PendingMatch) => {
      setModal({ isOpen: true, match, loading: false, error: null });
    },
    []
  );

  const handleModalClose = useCallback(() => {
    setModal(INITIAL_MODAL);
  }, []);

  const handleAcceptMatch = useCallback(async () => {
    if (!modal.match || !data?.userTeam) return;

    setModal((prev) => ({ ...prev, loading: true, error: null }));

    try {
      await axiosClient.post(`/matches/${modal.match.matchId}/accept`, {
        teamId: data.userTeam.teamId,
      });

      setModal(INITIAL_MODAL);
      refresh();
      navigate(`/matches/${modal.match.matchId}`);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Failed to accept match.';
      setModal((prev) => ({ ...prev, loading: false, error: msg }));
    }
  }, [modal.match, data?.userTeam, refresh, navigate]);

  const handleCancelMatch = useCallback(
    async (matchId: number) => {
      if (!data?.userTeam) return;

      try {
        await axiosClient.post(`/matches/${matchId}/cancel`, {
          teamId: data.userTeam.teamId,
        });
        refresh();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to cancel match.';
        setError(msg);
      }
    },
    [data?.userTeam, refresh]
  );

  const handleAcceptChallenge = useCallback(
    async (matchId: number) => {
      if (!data?.userTeam) return;

      try {
        await axiosClient.post(`/matches/${matchId}/accept`, {
          teamId: data.userTeam.teamId,
        });
        refresh();
        navigate(`/matches/${matchId}`);
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to accept challenge.';
        setError(msg);
      }
    },
    [data?.userTeam, refresh, navigate]
  );

  const handleDeclineChallenge = useCallback(
    async (matchId: number) => {
      if (!data?.userTeam) return;

      try {
        await axiosClient.post(`/matches/${matchId}/decline`, {
          teamId: data.userTeam.teamId,
        });
        refresh();
      } catch (err: any) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to decline challenge.';
        setError(msg);
      }
    },
    [data?.userTeam, refresh]
  );

  // No game selected
  if (!hasGame) {
    return (
      <div className="sl-dashboard">
        <div className="sl-dashboard-inner">
          <p className="sl-card-subtitle" style={{ textAlign: 'center', padding: '3rem 1rem' }}>
            Select a game from the navigation to view the dashboard.
          </p>
        </div>
      </div>
    );
  }

  // ── Modal content ─────────────────────────────────────

  const renderModalContent = () => {
    if (!modal.match) return null;

    const { match } = modal;
    const typeLabel   = formatMatchType(match.matchType);
    const formatLabel = formatMatchFormat(match.matchFormat);

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="sl-badge sl-badge--format">{formatLabel}</span>
          <span className="sl-badge sl-badge--state">{typeLabel}</span>
        </div>

        {match.scheduledAt && (
          <p style={{ fontSize: '0.82rem', color: 'var(--sl-text-muted)', margin: 0 }}>
            Scheduled for{' '}
            {new Date(match.scheduledAt).toLocaleString('en-US', {
              month:  'short',
              day:    'numeric',
              hour:   'numeric',
              minute: '2-digit',
            })}
          </p>
        )}

        <p style={{ fontSize: '0.85rem', color: 'var(--sl-text)', margin: 0 }}>
          Accept this {formatLabel} {typeLabel.toLowerCase()} match?
          Your team will be locked in once accepted.
        </p>

        {modal.error && (
          <p style={{ fontSize: '0.8rem', color: '#fca5a5', margin: 0 }}>
            {modal.error}
          </p>
        )}
      </div>
    );
  };

  // ── Render ────────────────────────────────────────────

  return (
    <div className="sl-dashboard">
      <div className="sl-dashboard-inner">

        {/* Season Header */}
        <SeasonHeader
          season={data?.season ?? null}
          loading={loading}
        />

        {/* Error Banner */}
        {error && (
          <div className="sl-dashboard-error">
            <span>{error}</span>
            <button className="sl-dashboard-error-retry" onClick={refresh}>
              Retry
            </button>
          </div>
        )}

        {/* Main Grid */}
        <div className="sl-dashboard-grid">

          {/* Left Column: Leaderboard */}
          <div className="sl-dashboard-section sl-dashboard-section--leaderboard">
            <LeaderboardPreview
              entries={data?.leaderboard ?? []}
              loading={loading}
              onViewFull={() => navigate('/leaderboard')}
              onTeamClick={(teamId: number) => navigate(`/teams/${teamId}`)}
            />
          </div>

          {/* Right Column: Available Matches + Match Center */}
          <div className="sl-dashboard-aside">
            <div className="sl-dashboard-section sl-dashboard-section--available">
              <AvailableMatches
                openMatches={data?.availableMatches?.openMatches ?? []}
                pendingMatches={data?.availableMatches?.userPendingMatches ?? []}
                loading={loading}
                userTeam={data?.userTeam ?? null}
                onMatchAction={handleMatchAction}
                onCancelMatch={handleCancelMatch}
                onBrowseAll={() => navigate('/matches')}
              />
            </div>

            <div className="sl-dashboard-section sl-dashboard-section--match-center">
              <MatchCenter
                activeMatches={data?.matchCenter?.activeMatches ?? []}
                incomingChallenges={data?.matchCenter?.incomingChallenges ?? []}
                userTeam={data?.userTeam ?? null}
                loading={loading}
                isAuthenticated={isAuthenticated}
                onNavigate={navigate}
                onAcceptChallenge={handleAcceptChallenge}
                onDeclineChallenge={handleDeclineChallenge}
              />
            </div>
          </div>

        </div>
        {/* ↑ End sl-dashboard-grid */}

        {/* Tip of the Hat — sibling to grid, spans full container width */}
        <div className="sl-tip-hat">
          <p className="sl-tip-hat-label">TIP OF THE HAT</p>
          <div className="sl-tip-hat-orgs">
            {TIP_OF_THE_HAT_ORGS.map((org) => (
              <a
                key={org.name}
                href={org.href}
                target="_blank"
                rel="noopener noreferrer"
                className="sl-tip-hat-org"
              >
                <img
                  src={org.logo}
                  alt={org.name}
                  className="sl-tip-hat-logo"
                />
                <span className="sl-tip-hat-name">{org.name}</span>
              </a>
            ))}
          </div>
          <p className="sl-tip-hat-message">
            Thank you to the organizations, individual community members, and everyone helping spread the word.
          </p>
          <p className="sl-tip-hat-message sl-tip-hat-message--bold">
            The entire Halo community is what will make Summit League possible.
          </p>
        </div>

        {/* Recent Matches — sibling to grid, full width */}
        <div className="sl-dashboard-section sl-dashboard-section--recent">
          <RecentMatches
            matches={data?.recentMatches ?? []}
            loading={loading}
            onMatchClick={(matchId: number) => navigate(`/matches/${matchId}`)}
          />
        </div>

        {/* Guest CTA */}
        {!isAuthenticated && !loading && (
          <GuestCta onSignIn={() => navigate('/auth')} />
        )}

        {/* Accept Match Modal */}
        <Modal
          isOpen={modal.isOpen}
          title="Accept Match"
          onConfirm={handleAcceptMatch}
          onCancel={handleModalClose}
          confirmLabel="Accept Match"
          cancelLabel="Close"
          disableConfirm={modal.loading}
          disableCancel={modal.loading}
        >
          {renderModalContent()}
        </Modal>

      </div>
    </div>
  );
};

export default DashboardPage;